export type RecordingUpdate = {
  durationMs: number;
  metering?: number;
};

export type PlaybackUpdate = {
  positionMs: number;
  durationMs: number;
};

// Duplicated verbatim from the native ChatAudioService.ts (not re-exported
// from it — under this project's `.web` module-suffix resolution, an
// extensionless `from './ChatAudioService'` written INSIDE this very file
// resolves back to itself, a circular self-import). Both are pure, platform-
// agnostic helpers with no logic to diverge.
export function formatAudioDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function createWaveformData(seed: string, count = 28): number[] {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return Array.from({ length: count }, (_, index) => {
    const value = Math.sin((hash + index * 37) * 0.017) * 0.5 + 0.5;
    return Math.max(0.16, Math.min(1, value));
  });
}

const SAMPLE_INTERVAL_MS = 80;

// Prefer a format the native players (AVAudioPlayer/MediaPlayer, via
// react-native-nitro-sound) can already decode — 'audio/mp4' (AAC), the
// exact container/codec native itself records. Only Safari's MediaRecorder
// currently supports encoding directly into it; Chrome/Firefox fall back to
// WebM/Opus, which plays fine on any web <audio> element but NOT on current
// native iOS/Android or in Safari (see the Phase 4 plan's cross-platform
// compatibility note) — VoiceMessageBubble.web-agnostic playback logic
// checks audioMimeType before attempting playback specifically because of
// this gap, rather than pretending every browser can produce one universal
// format.
const PREFERRED_MIME_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return '';
  for (const type of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

let activeStream: MediaStream | null = null;
let activeRecorder: MediaRecorder | null = null;
let recordedChunks: Blob[] = [];
let recordingStartedAt = 0;
let recordingMeterInterval: ReturnType<typeof setInterval> | null = null;
let lastRecordingMimeType: string | undefined;
let analyser: AnalyserNode | null = null;
let audioContext: AudioContext | null = null;

/** Web counterpart to native's export of the same name — reports the real MediaRecorder.mimeType most recently used, so the message can be tagged with its actual format (see useProfile.ts's sendChatMessage audioMimeType field). */
export function getLastRecordingMimeType(): string | undefined {
  return lastRecordingMimeType;
}

function stopMeterLoop(): void {
  if (recordingMeterInterval != null) {
    clearInterval(recordingMeterInterval);
    recordingMeterInterval = null;
  }
  analyser?.disconnect();
  analyser = null;
  audioContext?.close().catch(() => undefined);
  audioContext = null;
}

export async function getPlayableVoiceUri(remoteUrl: string): Promise<string> {
  // No local file cache needed on web — the browser's own HTTP cache
  // already handles repeat playback of the same remote URL.
  return remoteUrl;
}

export async function startVoiceRecording(onUpdate: (update: RecordingUpdate) => void): Promise<string> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  activeStream = stream;
  recordedChunks = [];
  const mimeType = pickMimeType();
  lastRecordingMimeType = mimeType || undefined;
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  activeRecorder = recorder;
  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) recordedChunks.push(event.data);
  });

  try {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(stream);
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
  } catch {
    analyser = null; // Metering is a nice-to-have — recording still works without it.
  }

  recordingStartedAt = Date.now();
  recorder.start();

  const levelBuffer = analyser ? new Uint8Array(analyser.fftSize) : null;
  recordingMeterInterval = setInterval(() => {
    let metering: number | undefined;
    if (analyser && levelBuffer) {
      analyser.getByteTimeDomainData(levelBuffer);
      let sumSquares = 0;
      for (let i = 0; i < levelBuffer.length; i += 1) {
        const normalized = (levelBuffer[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / levelBuffer.length);
      // Rough dBFS approximation — same "roughly -60..0" range the native
      // metering value already lands in, which is all VoiceRecorderComposer's
      // normalization (`(metering + 60) / 60`) actually assumes.
      metering = rms > 0 ? Math.max(-60, 20 * Math.log10(rms)) : -60;
    }
    onUpdate({ durationMs: Date.now() - recordingStartedAt, metering });
  }, SAMPLE_INTERVAL_MS);

  // Native's startRecorder() resolves with the eventual file URI immediately
  // (before recording finishes) — MediaRecorder has no equivalent (the blob
  // only exists once stopped), so this resolves with an empty string. The
  // one caller (VoiceRecorderComposer.tsx) only uses this as a fallback for
  // stopVoiceRecording()'s own (real) return value, never on its own.
  return '';
}

export async function pauseVoiceRecording(): Promise<void> {
  if (activeRecorder?.state === 'recording') activeRecorder.pause();
}

export async function resumeVoiceRecording(): Promise<void> {
  if (activeRecorder?.state === 'paused') activeRecorder.resume();
}

export async function stopVoiceRecording(): Promise<string> {
  stopMeterLoop();
  const recorder = activeRecorder;
  const stream = activeStream;
  activeRecorder = null;
  activeStream = null;
  if (!recorder) return '';

  const blob = await new Promise<Blob>((resolve) => {
    recorder.addEventListener('stop', () => {
      resolve(new Blob(recordedChunks, { type: lastRecordingMimeType || recorder.mimeType || 'audio/webm' }));
    }, { once: true });
    if (recorder.state !== 'inactive') recorder.stop();
    else resolve(new Blob(recordedChunks, { type: lastRecordingMimeType || 'audio/webm' }));
  });
  stream?.getTracks().forEach((track) => track.stop());
  recordedChunks = [];
  return URL.createObjectURL(blob);
}

// --- Playback ---------------------------------------------------------

let currentAudioEl: HTMLAudioElement | null = null;
let currentPlaybackInterval: ReturnType<typeof setInterval> | null = null;

function teardownPlayback(): void {
  if (currentPlaybackInterval != null) {
    clearInterval(currentPlaybackInterval);
    currentPlaybackInterval = null;
  }
  if (currentAudioEl) {
    currentAudioEl.pause();
    currentAudioEl.src = '';
    currentAudioEl = null;
  }
}

export async function startVoicePlayback(
  uri: string,
  speed: number,
  onUpdate: (update: PlaybackUpdate) => void,
  onEnd: () => void,
): Promise<void> {
  // Only one voice message plays at a time (matches native's single shared
  // AudioRecorderPlayer instance) — starting a new one interrupts whatever
  // was already playing.
  teardownPlayback();

  const audioEl = new Audio(uri);
  audioEl.playbackRate = speed;
  currentAudioEl = audioEl;

  audioEl.addEventListener('ended', () => {
    if (currentAudioEl === audioEl) onEnd();
  });

  currentPlaybackInterval = setInterval(() => {
    if (currentAudioEl !== audioEl) return;
    onUpdate({
      positionMs: audioEl.currentTime * 1000,
      durationMs: Number.isFinite(audioEl.duration) ? audioEl.duration * 1000 : 0,
    });
  }, SAMPLE_INTERVAL_MS);

  await audioEl.play();
}

export async function pauseVoicePlayback(): Promise<void> {
  currentAudioEl?.pause();
}

export async function resumeVoicePlayback(speed: number): Promise<void> {
  if (!currentAudioEl) return;
  currentAudioEl.playbackRate = speed;
  await currentAudioEl.play();
}

export async function stopVoicePlayback(): Promise<void> {
  teardownPlayback();
}

export async function seekVoicePlayback(ms: number): Promise<void> {
  if (currentAudioEl) currentAudioEl.currentTime = ms / 1000;
}

export async function setVoicePlaybackSpeed(speed: number): Promise<void> {
  if (currentAudioEl) currentAudioEl.playbackRate = speed;
}

/** Web counterpart to native's export of the same name — asks the browser's own <audio> element rather than hard-coding a fixed set, since decodable formats vary by browser (e.g. Safari can't play WebM at all, Chrome/Firefox can). */
export function canPlayAudioMimeType(mimeType?: string): boolean {
  if (!mimeType) return true;
  if (typeof document === 'undefined') return true;
  const probe = document.createElement('audio');
  return probe.canPlayType(mimeType) !== '';
}
